import { SerializedStyles, css } from "@emotion/react";
import styled from "@emotion/styled";
import memoizeOne from "memoize-one";
import { FC, ReactElement } from "react";

import { Error, PeopleFilled } from "@livechat/design-system-icons";
import { DesignToken, Icon, SpacingToken, Text, Tooltip } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { TicketsRoutes, getPathForRoute, routeTitles } from "constants/tickets/TicketsRoutes";
import { formatCounterValue, isCounterValueRounded } from "helpers/numbers";
import { ISideNavigationItem } from "interfaces/sideNavigationItem";
import MyViewsNavigationHeader from "routes/tickets/MyViewsNavigationHeader";
import SiloNavigationHeader from "routes/tickets/SiloNavigationHeader";
import StatusesNavigationHeader from "routes/tickets/StatusesNavigationHeader";
import { ICustomViewWithStatus } from "store/entities/custom-views/interfaces";
import { withVar } from "themes/helpers";

const StyledLi = styled.li`
  list-style: none;
  position: relative;
  padding: var(${SpacingToken.Spacing2}) 0;

  &:hover,
  &:focus,
  &:focus-within,
  &:active {
    .link-text {
      color: var(${DesignToken.ActionPrimaryDefault});
    }
    cursor: pointer;
  }
`;

const LinkText = styled(Text)<{ active: boolean }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  color: var(${DesignToken.ContentBasicSecondary});
  ${({ active }): SerializedStyles | string =>
    active
      ? css`
          color: var(${DesignToken.ActionPrimaryDefault});
          font-weight: 600;
        `
      : ""};
  outline: none;
  text-decoration: none;
  transition: color 0.2s ease-in-out;

  &:hover {
    color: var(${DesignToken.ActionPrimaryDefault});
  }
`;

function calculateNavigationItemCounter(
  route: string,
  navigationItemsCount?: Partial<Record<string, number>>
): number | null {
  if (!navigationItemsCount) {
    return null;
  }
  const itemsCount = navigationItemsCount[route];
  if (itemsCount === undefined) {
    return null;
  }

  return itemsCount;
}

function routeToNavigationItem(
  route: TicketsRoutes,
  navigationItemsCount?: Partial<Record<TicketsRoutes, number>>
): ISideNavigationItem {
  return {
    id: route,
    name: routeTitles[route],
    path: getPathForRoute(route),
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    render: ({ onItemClick, activeItemId }) => {
      const counter = calculateNavigationItemCounter(route, navigationItemsCount);

      return (
        // @ts-expect-error TS(2722)
        <StyledLi onClick={() => onItemClick(route)}>
          <Flex alignItems="baseline" justifyContent="space-between">
            <LinkText className="link-text" active={activeItemId === route}>
              {routeTitles[route]}
            </LinkText>
            {counter !== null && <CounterBadge count={counter} />}
          </Flex>
        </StyledLi>
      );
    },
  };
}

function undeliveredToNavigationItem(
  shouldShowNewFeatureBadge: boolean,
  navigationItemsCount?: Partial<Record<TicketsRoutes, number>>
): ISideNavigationItem {
  return {
    id: TicketsRoutes.Undelivered,
    name: "Undelivered",
    path: getPathForRoute(TicketsRoutes.Undelivered),
    render: ({ onItemClick, activeItemId }): ReactElement | null => {
      const counter = calculateNavigationItemCounter(TicketsRoutes.Undelivered, navigationItemsCount) ?? 0;

      if (counter === 0 && activeItemId !== TicketsRoutes.Undelivered) {
        return null;
      }

      return (
        <StyledLi onClick={() => onItemClick?.(TicketsRoutes.Undelivered)}>
          <Flex alignItems="center" justifyContent="space-between">
            <Flex gap={withVar(SpacingToken.Spacing2)}>
              <Icon source={Error} size="xsmall" customColor={withVar(DesignToken.ContentBasicSecondary)} />
              <LinkText
                data-testid="undelivered-view"
                className="link-text"
                active={activeItemId === TicketsRoutes.Undelivered}
              >
                Undelivered
              </LinkText>
              {shouldShowNewFeatureBadge && (
                <Text size="sm" bold customColor={withVar(DesignToken.ContentBasicNegative)}>
                  NEW!
                </Text>
              )}
            </Flex>
            {counter > 0 && <CounterBadge count={counter} />}
          </Flex>
        </StyledLi>
      );
    },
  };
}

function viewToNavigationItem(
  view: ICustomViewWithStatus,
  navigationItemsCount?: Partial<Record<string, number>>
): ISideNavigationItem {
  const counter = calculateNavigationItemCounter(view.id, navigationItemsCount);
  const isSharedView = view.agentID === null;

  return {
    id: view.id,
    name: view.name,
    render: ({ onItemClick, activeItemId }) => (
      <StyledLi onClick={() => (onItemClick ? onItemClick(view.id) : undefined)}>
        <Flex alignItems="center" justifyContent="space-between">
          <LinkText className="link-text" active={activeItemId === view.id}>
            <Flex alignItems="baseline" gap="12px">
              {isSharedView && (
                <Tooltip placement="top" theme="invert" triggerRenderer={<PeopleFilled width="12px" />}>
                  Shared ticket view
                </Tooltip>
              )}
              <span>{view.name}</span>
            </Flex>
          </LinkText>
          <Flex
            alignSelf="flex-start"
            paddingTop={withVar(SpacingToken.Spacing05)}
            data-testid="shared-view-counter-icon"
          >
            {view.invalid && (
              <Tooltip
                placement="left"
                kind="invert"
                triggerRenderer={
                  <Icon source={Error} size="small" customColor={withVar(DesignToken.ContentBasicSecondary)} />
                }
              >
                Invalid filter
              </Tooltip>
            )}
            {!view.invalid && counter !== null && <CounterBadge count={counter} />}
          </Flex>
        </Flex>
      </StyledLi>
    ),
  };
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
const ticketViewsHeader = (canManage: any) => ({
  // FIXME: Fix this the next time the file is edited.
  // @ts-expect-error TS(2741): Property 'title' is missing in type '{ canManage: ... Remove this comment to see the full error message
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  render: () => <MyViewsNavigationHeader canManage={canManage} />,
});

const siloHeader = {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  render: () => <SiloNavigationHeader />,
};

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const siloViews = () =>
  [TicketsRoutes.Archive, TicketsRoutes.Spam, TicketsRoutes.Trash].map((route: TicketsRoutes) =>
    routeToNavigationItem(route)
  );

const statusHeader = {
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  render: () => <StatusesNavigationHeader />,
};

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const statusViews = (navigationItemsCount?: Partial<Record<string, number>>) =>
  [TicketsRoutes.Open, TicketsRoutes.Pending, TicketsRoutes.OnHold, TicketsRoutes.Solved, TicketsRoutes.Closed].map(
    (route: TicketsRoutes) => routeToNavigationItem(route, navigationItemsCount)
  );

export const getTicketNavigationItems = memoizeOne(
  (
    customViews: ICustomViewWithStatus[],
    canSeeCustomViews: boolean,
    canCreateCustomViews: boolean,
    navigationItemsCount: Partial<Record<TicketsRoutes, number>>,
    hasUndeliveredTickets: boolean,
    shouldShowNewFeatureBadge: boolean
  ): ISideNavigationItem[] => {
    const ticketViewsItems = customViews.map(view => viewToNavigationItem(view, navigationItemsCount));

    return [
      ...[TicketsRoutes.All, TicketsRoutes.Unassigned, TicketsRoutes.MyOpen].map((route: TicketsRoutes) =>
        routeToNavigationItem(route, navigationItemsCount)
      ),
      ...(hasUndeliveredTickets ? [undeliveredToNavigationItem(shouldShowNewFeatureBadge, navigationItemsCount)] : []),
      ...(canSeeCustomViews && ticketViewsItems.length > 0 ? [ticketViewsHeader(canCreateCustomViews)] : []),
      ...(canSeeCustomViews ? ticketViewsItems : []),
      statusHeader,
      ...statusViews(navigationItemsCount),
      siloHeader,
      ...siloViews(),
    ];
  }
);

const CounterBadge: FC<{ count: number }> = ({ count }) => {
  const display = <CounterBadgePill data-testid="counter-badge">{formatCounterValue(count)}</CounterBadgePill>;

  if (!isCounterValueRounded(count)) {
    return display;
  }

  return (
    <Tooltip placement="top" kind="invert" triggerRenderer={display}>
      {count.toLocaleString("en-US")}
    </Tooltip>
  );
};

const CounterBadgePill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  height: 20px;
  min-width: 20px;
  padding: 2px 6px;
  border-radius: 20px;
  background-color: var(${DesignToken.SurfaceTertiaryDefault});
  color: var(${DesignToken.ContentBasicSecondary});
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
`;
